import {
  imgElementSvg,
  vesselIconCargoShip,
  vesselIconCruiseShip,
  vesselIconFerry,
  vesselIconFishingBoat,
  vesselIconMilitaryShip,
  vesselIconMotorBoat,
  vesselIconPilotBoat,
  vesselIconRescueBoat,
  vesselIconSailBoat,
  vesselIconSmallBoat,
  vesselIconSpeedBoat,
  vesselIconSuperyacht,
  vesselIconTanker,
  vesselIconTugBoat,
} from '../../../components/shared/vector-images';
import { callEndpoint, telemetry } from '../../backend';
import { definitions, subscribe } from '../../message-broker';
import {
  layerExists,
  layerVisible,
  setDataSource,
  sourceExists,
  toggleLayerIfRequired,
} from '../_helpers';

let mapReference;
let selectedMMSI = null;

const getAisData = (boundingBox) => {
  // Use [minLon,minLat,maxLon,maxLat] format
  const convertBoundingBox = [
    boundingBox[0].lon,
    boundingBox[1].lat,
    boundingBox[1].lon,
    boundingBox[0].lat,
  ];
  telemetry(telemetry.DEBUG, `[queryAIS] ${convertBoundingBox}`);
  return callEndpoint(callEndpoint.endpoints.queryAis, {
    boundingBox: convertBoundingBox,
  });
};

const addLayersIfRequired = () => {
  if (!sourceExists(mapReference, 'ais')) {
    mapReference.addSource('ais', {
      type: 'geojson',
      data: {
        type: 'FeatureCollection',
        features: [],
      },
    });
  }
  if (!layerExists(mapReference, 'ais')) {
    mapReference.addLayer({
      id: 'ais',
      type: 'symbol',
      source: 'ais',
      layout: {
        'icon-image': ['to-string', ['get', 'icon']],
        'icon-size': [
          'interpolate',
          ['linear'],
          ['zoom'],
          8.4999,
          ['case', ['==', ['get', 'markerSelected'], true], 0.5, 0],
          8.5,
          ['case', ['==', ['get', 'markerSelected'], true], 0.5, 0.2],
          11,
          ['case', ['==', ['get', 'markerSelected'], true], 0.5, 0.2],
          14,
          ['case', ['==', ['get', 'markerSelected'], true], 0.5, 0.4],
        ],
        'icon-anchor': 'bottom',
        'icon-allow-overlap': true,
        'text-allow-overlap': true,
      },
      paint: {},
    });
  }
};

const layer = {
  displayName: 'Vessel tracking',
  synonyms: '',
  group: 'tileLayer',
  typeUrl: 'vessel-tracking',
  icon: {
    baseShape: vesselIconCargoShip,
    iconColor: '#fff',
    markerColor: '#fff',
  },
  shownByDefault: true,
  relativeDepth: 10,
  menuOptions: {
    visible: true,
    owningMenu: 'vessel-tracking',
    order: 0,
  },
};

let requestDataTimeout;
let currentViewPort;
let lastQueryViewPort;

const aisDataCache = {};

const updateDataSource = async (map, fetch = true) => {
  if (!layerVisible(map, 'ais')) return;
  if (fetch || !aisDataCache.length) {
    const newData = await getAisData(currentViewPort.bounds);
    if (newData.results.length > 0) {
      newData.results.forEach((item) => {
        aisDataCache[`${item?.boatPayload?.mmsi}`] = item;
      });
    }
  }

  setDataSource(map, 'ais', {
    type: 'FeatureCollection',
    features: Object.entries(aisDataCache).map(([_, item]) => ({
      type: 'Feature',
      geometry: {
        type: 'Point',
        coordinates: [item.longitude, item.latitude],
      },
      properties: {
        icon: item?.boatPayload?.icon,
        mmsi: item?.boatPayload?.mmsi,
        type: item?.boatPayload?.type,
        shipName: item?.boatPayload?.shipName,
        speed: item?.speed,
        timestamp: item?.timestamp,
        markerSelected: item?.boatPayload?.mmsi === selectedMMSI,
      },
    })),
  });
};

const selectBoatMarker = async (map, mmsiToSelect) => {
  selectedMMSI = mmsiToSelect;
  await updateDataSource(map, false);
};

layer.onInitialize = (map) => {
  mapReference = map;
  addLayersIfRequired();

  setTimeout(async () => {
    const icons = {
      vesselIconSmallBoat,
      vesselIconFishingBoat,
      vesselIconSailBoat,
      vesselIconMotorBoat,
      vesselIconSpeedBoat,
      vesselIconTugBoat,
      vesselIconFerry,
      vesselIconSuperyacht,
      vesselIconCruiseShip,
      vesselIconCargoShip,
      vesselIconTanker,
      vesselIconPilotBoat,
      vesselIconRescueBoat,
      vesselIconMilitaryShip,
    };
    Object.keys(icons).forEach(async (iconName) => {
      if (!mapReference.hasImage(iconName)) {
        const marker = await imgElementSvg(icons[iconName](), {
          width: 100,
          height: 100,
        });
        mapReference.addImage(iconName, marker);
      }
    });
  }, 500);

  mapReference.on('deckee-press', 'ais', async (event) => {
    if (!layerVisible(mapReference, 'ais')) return;
    // This is to select a marker and go to the popover
    const { mmsi } = event.features[0].properties;

    selectBoatMarker(mapReference, mmsi);
    // Prevent the map click event from propagating to other layers
    event.originalEvent.preventDefault();
  });
};

const updateData = async (force) => {
  // If we have yet to receive a reference to the map object through the onInitialize function,
  // just return as we can't update the data on the layer if the layer doesn't exist yet.
  if (!mapReference) return;
  // If not layer is not enabled to user don't update
  if (!layerVisible(mapReference, 'ais')) return;
  addLayersIfRequired();

  // If there isn't a view port to query for bounds, don't do anything.
  if (!currentViewPort) return;

  // If we're not forcing the update, and the bounds are the same as the
  // previous call, don't do anything
  if (!force && currentViewPort && currentViewPort.bounds === lastQueryViewPort)
    return;
  lastQueryViewPort = currentViewPort && currentViewPort.bounds;

  // Update the data for this layer every minute when the viewport doesn't change.
  if (requestDataTimeout) clearTimeout(requestDataTimeout);
  requestDataTimeout = setTimeout(() => updateData(true), 60 * 1000);

  await updateDataSource(mapReference, true);
};

layer.onToggle = (map, show) => {
  const currentlyVisible = layerVisible(map, 'ais');
  if (show && !currentlyVisible) {
    updateData();
  }
  toggleLayerIfRequired(map, 'ais', show);
};

subscribe(definitions.map.viewPortUpdated, (viewPort) => {
  currentViewPort = viewPort;
  updateData();
});

export default layer;
